<script setup>
import { onMounted, ref, computed } from 'vue'
import axios from 'axios'
import numeral from 'numeral'

import Header from '@/components/Header.vue'
import Pulsating from '@/components/Pulsating.vue'

const emit = defineEmits(['changeMode'])

const keyUses = [
  {
    header: 'GOVERNANCE',
    desc: 'Participate in the decision-making process for future developments.',
  },
  {
    header: 'STAKING',
    desc: `Stake $DEXTF to earn rewards and participate in Memento's ecosystem. <a href="https://staking.dextf.com" target="staking-dapp"><i class="external alternate small icon secondary"></i></a>`,
  },
  {
    header: 'TRANSACTION FEES',
    desc: 'Earn transaction fees on Memento DFM and Memento ZK Chain.',
  },
  {
    header: 'LIQUIDITY PROVISION',
    desc: 'Provide liquidity on decentralized exchanges and earn rewards.',
  },
  {
    header: 'ECOSYSTEM GROWTH',
    desc: 'Access new products and services within the Memento ecosystem.',
  },
]

const circulatingSupply = ref(null)
const totalSupply = ref(null)
const tokenPrice = ref(null)
const tokenPriceFormatted = ref(null)

const stats = computed(() => [
  {
    label: 'Circulating Supply',
    value: numeral(circulatingSupply.value).format('0,0.00a') || 'Loading...',
  },
  {
    label: 'Market Cap',
    value: numeral(circulatingSupply.value * tokenPrice.value).format('0,0.00a') || 'Loading...',
  },
  { label: 'Total Supply', value: numeral(totalSupply.value).format('0,0.00a') || 'Loading...' },
])

onMounted(async () => {
  emit('changeMode', 2)

  try {
    const [circulating, total, price] = await Promise.all([
      axios.get('https://api.dextf.com/dcp/token/supply/circulating'),
      axios.get('https://api.dextf.com/dcp/token/supply/total'),
      axios.get('https://api.coingecko.com/api/v3/simple/price', {
        params: {
          ids: 'dextf',
          vs_currencies: 'usd',
          include_market_cap: false,
          include_24hr_vol: false,
          include_24hr_change: false,
          include_last_updated_at: false,
          precision: 5,
        },
        headers: {
          accept: 'application/json',
          'x-cg-demo-api-key': import.meta.env.VITE_COINGECKO_API_KEY,
        },
      }),
    ])

    circulatingSupply.value = circulating.data
    totalSupply.value = total.data
    tokenPrice.value = price.data.dextf.usd
    tokenPriceFormatted.value = numeral(tokenPrice.value).format('0,0.00000a')
  } catch (error) {
    console.error('Failed to fetch token data:', error)
    circulatingSupply.value = 'N/A'
    totalSupply.value = 'N/A'
    tokenPrice.value = 'N/A'
    tokenPriceFormatted.value = 'N/A'
  }
})
</script>

<template>
  <div id="dextfView" style="padding-top: 150px">
    <div class="ui vertical segment">
      <!-- Hero -->
      <div
        class="ui basic segment container _bgImage"
        style="
          background-image: linear-gradient(
              360deg,
              rgba(19, 21, 27, 0.6) 0%,
              rgba(19, 21, 27, 0.24) 100%
            ),
            url('./images/dextf_bg.webp');
        "
      >
        <Pulsating
          :image-src="'/images/dextf_with_halo.png'"
          style="position: absolute; top: 12%; right: 5%"
        ></Pulsating>
        <table style="width: 100%; height: 100%; border-spacing: 0">
          <tbody>
            <tr style="height: 1%">
              <td>
                <div
                  class="ui inverted massive header"
                  style="padding: 0 !important; position: relative"
                >
                  <img
                    class="ui massive image"
                    src="/images/Memento_icon_whiteFG_transparentBG.png"
                  />
                  <div class="content" style="max-width: 50vw">Memento $DEXTF</div>
                </div>
              </td>
            </tr>
            <tr style="height: 98%">
              <td>
                <span class="ui large text">
                  $DEXTF token, the backbone of Memento's ecosystem.
                </span>
              </td>
            </tr>
            <tr style="height: 1%">
              <td>
                <a
                  class="ui button _equalButton"
                  href="https://www.mexc.com/exchange/DEXTF_USDT"
                  target="dextf-mexcp"
                >
                  Trade on MEXC
                </a>
                <a
                  class="ui button _equalButton"
                  href="https://app.uniswap.org/swap?inputCurrency=ETH&outputCurrency=0x5f64ab1544d28732f0a24f4713c2c8ec0da089f0"
                  target="dextf-uniswap"
                >
                  Trade on Uniswap
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- What is Memento $DEXTF? -->
    <div class="ui vertical segment">
      <div class="ui center aligned basic segment container">
        <div class="ui one column grid" style="margin: 0">
          <div class="tablet computer only column">
            <Header text="What is Memento $DEXTF?" size="massive" animation="fade up in" />
          </div>
          <div class="mobile only column">
            <Header text="What is<br/>Memento $DEXTF?" size="massive" animation="fade up in" />
          </div>
        </div>

        <p
          class="_dynamicTextAlign"
          style="max-width: 55rem; margin-left: auto; margin-right: auto"
        >
          <span class="ui large text">
            Originally designed as the governance token for the
            <RouterLink to="/dfm-protocol">DFM</RouterLink> protocol, $DEXTF has evolved to become
            the central utility token of the entire <b>Memento</b> ecosystem.
            <br />
            As <b>Memento</b> continues to expand its offerings with the launch of its institutional
            layer-2 chain, $DEXTF now plays a pivotal role for both
            <RouterLink to="/dfm-protocol">DFM</RouterLink> and
            <RouterLink to="/zk-chain">ZK Chain</RouterLink>, enabling users to interact seamlessly
            within our decentralized financial infrastructure. From <b>governance</b> to
            <b>staking</b>
            <a href="https://staking.dextf.com" target="staking-dapp">
              <i
                class="external alternate small icon secondary"
                style="padding-left: 0.5rem"
              ></i> </a
            >, and from <b>liquidity provision</b> to <b>fee earning</b>, $DEXTF is the backbone of
            <b>Memento</b>'s decentralized ecosystem.
          </span>
        </p>
      </div>
    </div>

    <!-- Utility of Memento $DEXTF? -->
    <div class="ui vertical segment">
      <div class="ui basic segment container">
        <div class="ui stackable grid" style="padding-top: 2rem">
          <div class="two column row">
            <div class="column">
              <Header
                text="Utility of<br/>Memento $DEXTF"
                size="massive"
                animation="fade left in"
              />
            </div>
            <div v-for="(keyUse, index) in keyUses" class="column">
              <div class="ui big list">
                <div class="item">
                  <div class="content">
                    <div class="ui large header">
                      <i class="check icon secondary"></i>
                      <div class="content">{{ keyUse.header }}</div>
                    </div>
                    <span class="ui large text" v-html="keyUse.desc"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Information -->
    <div class="ui vertical segment" style="margin-bottom: 2rem">
      <div
        class="ui center aligned segment container _bgImage2"
        style="
          background-image: linear-gradient(
              360deg,
              rgba(0, 7, 82, 0.6) 0%,
              rgba(0, 7, 82, 0.3) 100%
            ),
            url('./images/dextf_info_bg.webp');
        "
      >
        <div class="ui three stackable center aligned cards _stats">
          <div v-for="(stat, index) in stats" :key="index" class="basic card">
            <div class="content">
              <div class="ui statistic inverted" style="margin-bottom: 32px">
                <div class="ui massive text value">{{ stat.value }}</div>
                <div class="label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="ui stackable center aligned grid">
          <div class="stretched three column row">
            <div class="column">
              <div class="ui fluid card _cta">
                <div class="image">
                  <img src="/images/networks/ethereum_horizontal.png" />
                </div>
                <div class="content">
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://www.mexc.com/exchange/DEXTF_USDT"
                    target="dextf-mexcp"
                  >
                    Trade on MEXC
                  </a>
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://app.uniswap.org/swap?inputCurrency=ETH&outputCurrency=0x5f64ab1544d28732f0a24f4713c2c8ec0da089f0"
                    target="dextf-uniswap"
                  >
                    Trade on Uniswap
                  </a>
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://app.uniswap.org/positions/create/v2?currencyA=0x5f64ab1544d28732f0a24f4713c2c8ec0da089f0&currencyB=ETH"
                    target="dextf-uniswap"
                  >
                    Add Liquidity
                  </a>
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://staking.dextf.com"
                    target="staking-dapp"
                  >
                    Staking
                  </a>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="ui fluid card _cta">
                <div class="image">
                  <img src="/images/networks/zkSyncEra_horizontal.png" />
                </div>
                <div class="content">
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://portal.zksync.io/bridge"
                    target="dextf-zksync-bridge"
                  >
                    Native Bridge
                  </a>
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://staking.dextf.com"
                    target="staking-dapp"
                  >
                    Staking
                  </a>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="ui fluid card _cta">
                <div class="image">
                  <img src="/images/partners/coingecko.png" />
                </div>
                <div class="content">
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://www.coingecko.com/en/coins/dextf"
                    target="dextf-coingecko"
                  >
                    See Token Page
                  </a>
                  <p style="margin-top: 2rem">
                    <span class="ui large text" style="color: white">
                      Price {{ tokenPriceFormatted }} USD
                    </span>
                  </p>
                </div>
              </div>
              <div class="ui fluid card _cta">
                <div class="image">
                  <img src="/images/partners/chainlink.png" />
                </div>
                <div class="content">
                  <a
                    class="ui basic fluid button _blurredGlass"
                    href="https://bridge.dextf.com"
                    target="dextf-bridge"
                  >
                    Multi-Chain Bridge
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.column:nth-child(2) > .ui.big.list {
  margin-top: 2rem !important;
}

.column:nth-child(n + 3) > .ui.big.list {
  margin-top: 3rem !important;
}

.ui.big.list > .item > .content > .header {
  margin-bottom: 0.7rem !important;
}

.ui.cards > .card,
.ui.card {
  border: none !important;
  box-shadow: none !important;
  background-color: rgba(255, 255, 255, 0.2);
}

.ui.cards._stats > .card,
.ui.card._stats {
  background-color: transparent;
}

.ui.cards._stats > .card > .content,
.ui.card._stats > .content {
  padding: 0;
}

.ui.card._cta > .content {
  padding: 2rem;
}

.ui.card._cta > .content > .button {
  margin-bottom: 1rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 350px;
}

.ui.card._cta > .image {
  x_background-color: transparent !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 !important;
  height: 5rem;
  filter: invert(1);
}

.ui.card._cta > .image > img {
  width: 70%;
  max-width: 180px !important;
}

._bgImage {
  height: var(--hero-height);
  padding: 4rem;
  border-radius: 24px !important;
  background-color: #000752 !important;
  background-size: cover !important;
  background-repeat: no-repeat !important;
  background-position: top right !important;
}

._bgImage .text {
  color: #effbff !important;
}

._bgImage2 {
  padding: 32px !important;
  background-color: #000752;
  background-size: cover !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
}

@media screen and (max-width: 700px) {
  ._bgImage {
    padding: 2rem;
  }

  ._bgImage2 {
    background-size: 200% auto !important;
    background-position: center bottom !important;
  }
}

@media screen and (min-width: 701px) and (max-width: 990px) {
  ._bgImage {
    padding: 2rem;
  }

  ._bgImage2 {
    background-size: cover !important;
    background-position: center top !important;
  }
}

._blurredGlass {
  backdrop-filter: blur(12px) !important;
  background: rgba(200, 200, 200, 0.2) !important;
  color: white !important;
}

._blurredGlass:hover {
  background: rgba(19, 21, 27, 0.3) !important;
}

._blurredGlass2 {
  backdrop-filter: blur(12px) !important;
  background: rgba(19, 21, 27, 0.08);
}

._equalButton {
  width: 12rem;
  margin: 0 1rem 1rem 0;
}

._dynamicTextAlign {
  text-align: left;
}

@media screen and (min-width: 700px) {
  ._dynamicTextAlign {
    text-align: justify;
  }
}
</style>
